import { useEffect, useState } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { ArrowLeft, User, Calendar, MessageSquare, Wrench, Brain, AlertCircle } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { FileArtifactsView, type FileArtifact } from './FileArtifactsView';
import { StudyArtifactView, type StudyArtifact } from './StudyArtifactView';

interface ThreadStep {
  id: string;
  type: string;
  name?: string;
  input?: string | object;
  output?: string | object;
  start_time?: string;
  end_time?: string;
  parent_id?: string;
  metadata?: any;
  tags?: string[];
  isError?: boolean;
  /** plan/kartice/kviz/mapa payload, present when the turn rendered a study element */
  study_artifact?: StudyArtifact;
  /** Files handed to the student on this turn (generated PDF/Word/PowerPoint, images) */
  file_artifacts?: FileArtifact[];
}

interface ThreadDetails {
  id: string;
  name: string;
  createdAt: string;
  userIdentifier?: string;
  userId?: string;
  tags: string[];
  metadata: {
    category?: string;
  };
  steps: ThreadStep[];
}

const AdminChatDetails = () => {
  const { t } = useTranslation();
  const { threadId } = useParams<{ threadId: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [thread, setThread] = useState<ThreadDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Get the return page from URL params to navigate back to the correct page
  const returnPage = searchParams.get('returnPage') || '1';

  useEffect(() => {
    if (threadId) {
      fetchThreadDetails(threadId);
    }
  }, [threadId]);

  const fetchThreadDetails = async (id: string) => {
    try {
      setLoading(true);
      setError(null);
      const response = await fetch('/api/threads', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          operation: 'get_thread_details',
          thread_id: id,
          include_tool_calls: true
        })
      });

      if (!response.ok) {
        throw new Error('Failed to fetch thread details');
      }

      const data = await response.json();
      setThread(data);
    } catch (error) {
      console.error('Error fetching thread details:', error);
      setError('Failed to load conversation details');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleString();
    } catch {
      return dateString;
    }
  };

  const formatDuration = (start: string, end: string) => {
    if (!start || !end) return null;
    try {
      const startTime = new Date(start).getTime();
      const endTime = new Date(end).getTime();
      const duration = (endTime - startTime) / 1000; // in seconds
      if (duration < 1) return `${(duration * 1000).toFixed(0)}ms`;
      return `${duration.toFixed(2)}s`;
    } catch {
      return null;
    }
  };

  const getStepIcon = (type: string) => {
    switch (type) {
      case 'user_message':
        return <User className="h-5 w-5" />;
      case 'assistant_message':
        return <MessageSquare className="h-5 w-5" />;
      case 'tool':
        return <Wrench className="h-5 w-5" />;
      case 'llm':
        return <Brain className="h-5 w-5" />;
      default:
        return <MessageSquare className="h-5 w-5" />;
    }
  };

const getStepLabel = (type: string) => {
    switch (type) {
      case 'user_message':
        return t('admin.chatDetails.stepTypes.user', 'User Message');
      case 'assistant_message':
        return t('admin.chatDetails.stepTypes.assistant', 'Assistant Message');
      case 'tool':
        return t('admin.chatDetails.stepTypes.tool', 'Tool Call');
      case 'llm':
        return t('admin.chatDetails.stepTypes.llm', 'LLM Processing');
      default:
        return type;
    }
  };

  const renderContent = (content: string | object | undefined) => {
    if (!content) return <span className="text-muted-foreground italic">No content</span>;
    
    if (typeof content === 'string') {
      return <pre className="whitespace-pre-wrap break-words font-sans">{content}</pre>;
    }
    
    // For objects, display as formatted JSON
    return (
      <pre className="whitespace-pre-wrap break-words text-sm overflow-x-auto">
        {JSON.stringify(content, null, 2)}
      </pre>
    );
  };

  if (loading) {
    return (
      <div>
        <Button variant="ghost" onClick={() => navigate(`/admin/chat-logs?page=${returnPage}`)} className="mb-4">
          <ArrowLeft className="h-4 w-4 mr-2" />
          {t('admin.chatDetails.back', 'Back to Chat Logs')}
        </Button>
        <Card>
          <CardHeader>
            <Skeleton className="h-8 w-64" />
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {Array.from({ length: 3 }).map((_, i) => (
                <Skeleton key={i} className="h-32 w-full" />
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error || !thread) {
    return (
      <div>
        <Button variant="ghost" onClick={() => navigate(`/admin/chat-logs?page=${returnPage}`)} className="mb-4">
          <ArrowLeft className="h-4 w-4 mr-2" />
          {t('admin.chatDetails.back', 'Back to Chat Logs')}
        </Button>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-2 text-destructive">
              <AlertCircle className="h-5 w-5" />
              <p>{error || t('admin.chatDetails.notFound', 'Thread not found')}</p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="mb-6">
        <Button variant="ghost" onClick={() => navigate(`/admin/chat-logs?page=${returnPage}`)} className="mb-4">
          <ArrowLeft className="h-4 w-4 mr-2" />
          {t('admin.chatDetails.back', 'Back to Chat Logs')}
        </Button>
        
        <Card>
          <CardHeader>
            <div className="flex items-start justify-between">
              <div className="space-y-2 flex-1">
                <CardTitle className="text-2xl">{thread.name || t('admin.chatDetails.untitled', 'Untitled Conversation')}</CardTitle>
                <div className="flex flex-wrap gap-4 text-sm text-muted-foreground">
                  <div className="flex items-center gap-2">
                    <User className="h-4 w-4" />
                    <span>{thread.userIdentifier || t('admin.chatDetails.anonymous', 'Anonymous User')}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Calendar className="h-4 w-4" />
                    <span>{formatDate(thread.createdAt)}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <MessageSquare className="h-4 w-4" />
                    <span>{thread.steps?.length || 0} {t('admin.chatDetails.steps', 'steps')}</span>
                  </div>
                </div>
                {thread.tags && thread.tags.length > 0 && (
                  <div className="flex gap-2 flex-wrap pt-2">
                    {thread.tags.map((tag, idx) => (
                      <Badge key={idx} variant="secondary">{tag}</Badge>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </CardHeader>
        </Card>
      </div>

      {/* Conversation Flow - Chat Style */}
      <Card>
        <CardHeader>
          <CardTitle>{t('admin.chatDetails.conversationFlow', 'Conversation Flow')}</CardTitle>
        </CardHeader>
        <CardContent className="bg-muted/30 min-h-[400px]">
          {!thread.steps || thread.steps.length === 0 ? (
            <p className="text-muted-foreground text-center py-8">
              {t('admin.chatDetails.noSteps', 'No conversation steps found')}
            </p>
          ) : (
            <div className="space-y-4 py-4">
              {thread.steps.map((step, idx) => {
                const isUser = step.type === 'user_message';
                const isAssistant = step.type === 'assistant_message';
                const isTool = step.type === 'tool';
                const isLLM = step.type === 'llm';
                
                return (
                  <div key={step.id || idx} className={`flex ${isUser ? 'justify-end' : 'justify-start'}`}>
                    <div className={`max-w-[80%] ${isUser ? 'ml-12' : 'mr-12'}`}>
                      {/* Message bubble */}
                      <div
                        className={`rounded-lg p-4 shadow-sm ${
                          isUser
                            ? 'bg-blue-500 text-white rounded-tr-none'
                            : isAssistant
                            ? 'bg-white dark:bg-gray-800 rounded-tl-none border'
                            : isTool
                            ? 'bg-purple-100 dark:bg-purple-950 border border-purple-300 dark:border-purple-800 rounded-tl-none'
                            : isLLM
                            ? 'bg-amber-100 dark:bg-amber-950 border border-amber-300 dark:border-amber-800 rounded-tl-none'
                            : 'bg-gray-100 dark:bg-gray-900 border rounded-tl-none'
                        }`}
                      >
                        {/* Header with type and timing */}
                        <div className={`flex items-center gap-2 mb-2 text-xs ${isUser ? 'text-blue-100' : 'text-muted-foreground'}`}>
                          <div className="flex items-center gap-1.5">
                            {getStepIcon(step.type)}
                            <span className="font-semibold">{getStepLabel(step.type)}</span>
                            {step.name && !isUser && !isAssistant && (
                              <Badge variant="outline" className="ml-1 text-xs">{step.name}</Badge>
                            )}
                          </div>
                          <span className="ml-auto">
                            {step.start_time && formatDate(step.start_time).split(',')[1]?.trim()}
                            {formatDuration(step.start_time || '', step.end_time || '') && (
                              <span className="ml-1.5">({formatDuration(step.start_time || '', step.end_time || '')})</span>
                            )}
                          </span>
                        </div>

                        {/* Error badge */}
                        {step.isError && (
                          <Badge variant="destructive" className="mb-2">
                            <AlertCircle className="h-3 w-3 mr-1" />
                            Error
                          </Badge>
                        )}

                        {/* Tool/LLM Input - Collapsible */}
                        {(isTool || isLLM) && step.input && (
                          <details className="mb-3">
                            <summary className={`text-xs font-medium cursor-pointer ${isUser ? 'text-blue-100' : 'text-muted-foreground'} fongpt-hover-text select-none`}>
                              {t('admin.chatDetails.input', 'Input')} ▶
                            </summary>
                            <div className="mt-2 bg-black/5 dark:bg-white/5 rounded p-2 text-sm">
                              {renderContent(step.input)}
                            </div>
                          </details>
                        )}

                        {/* Main Content */}
                        <div className={`text-sm ${isUser ? 'text-white' : ''}`}>
                          {step.type === 'user_message' && step.input ? (
                            renderContent(step.input)
                          ) : step.output ? (
                            <>
                              {(isTool || isLLM) ? (
                                <details>
                                  <summary className={`text-xs font-medium cursor-pointer ${isUser ? 'text-blue-100' : 'text-muted-foreground'} fongpt-hover-text select-none`}>
                                    {t('admin.chatDetails.output', 'Output')} ▶
                                  </summary>
                                  <div className="mt-2 bg-black/5 dark:bg-white/5 rounded p-2">
                                    {renderContent(step.output)}
                                  </div>
                                </details>
                              ) : (
                                renderContent(step.output)
                              )}
                            </>
                          ) : step.study_artifact || step.file_artifacts?.length ? null : (
                            <span className="italic opacity-60">
                              {t('admin.chatDetails.noContent', 'No content')}
                            </span>
                          )}
                        </div>

                        {/* Generated documents: the turn is an empty message
                            plus a file element, so name the file instead. */}
                        {step.file_artifacts?.length ? (
                          <div className="mt-2">
                            <FileArtifactsView artifacts={step.file_artifacts} />
                          </div>
                        ) : null}

                        {/* Study artifact (plan/kartice/kviz/mapa): the payload lives
                            in the step's elements, not in its text output. */}
                        {step.study_artifact && (
                          <div className="mt-2">
                            <StudyArtifactView artifact={step.study_artifact} />
                          </div>
                        )}

                        {/* Metadata (collapsible) */}
                        {step.metadata && Object.keys(step.metadata).length > 0 && (
                          <details className="mt-3">
                            <summary className={`text-xs cursor-pointer ${isUser ? 'text-blue-100' : 'text-muted-foreground'} fongpt-hover-text`}>
                              {t('admin.chatDetails.metadata', 'View Metadata')}
                            </summary>
                            <div className="mt-2 bg-black/10 dark:bg-white/10 rounded p-2">
                              <pre className="text-xs overflow-x-auto">
                                {JSON.stringify(step.metadata, null, 2)}
                              </pre>
                            </div>
                          </details>
                        )}

                        {/* Tags */}
                        {step.tags && step.tags.length > 0 && (
                          <div className="mt-2 flex gap-1 flex-wrap">
                            {step.tags.map((tag, tagIdx) => (
                              <Badge key={tagIdx} variant="secondary" className="text-xs">
                                {tag}
                              </Badge>
                            ))}
                          </div>
                        )}
                      </div>

                      {/* Timestamp below bubble */}
                      <div className={`text-xs text-muted-foreground mt-1 px-2 ${isUser ? 'text-right' : 'text-left'}`}>
                        {step.start_time && formatDate(step.start_time)}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminChatDetails;
